/**
 * Global Preloader and Scroll-to-Top Management
 * Handles preloader display until all images and critical scripts are loaded
 * Provides unified scroll-to-top functionality for all sections
 */

// Preloader state
let preloaderInitialized = false;
let imagesLoaded = false;
let scriptsReady = false;

// Unified scroll-to-top function (used by all sections)
function ensureScrollToTop(callback) {
  if (
    window.scrollY > 0 ||
    window.pageYOffset > 0 ||
    document.documentElement.scrollTop > 0
  ) {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    function checkScrollComplete() {
      if (
        window.scrollY === 0 &&
        window.pageYOffset === 0 &&
        document.documentElement.scrollTop === 0
      ) {
        callback();
      } else {
        requestAnimationFrame(checkScrollComplete);
      }
    }

    requestAnimationFrame(checkScrollComplete);
  } else {
    callback();
  }
}

// Make it globally available
window.ensureScrollToTop = ensureScrollToTop;

/**
 * Section Progress Bar Utility
 * Creates and manages progress bars for multi-state sections
 * @param {HTMLElement} section - The section element
 * @param {number} totalStates - Total number of states (default: 4)
 * @returns {Object} - Object with updateProgress method
 */
function createSectionProgressBar(section, totalStates = 4) {
  if (!section) return null;

  // Create progress bar container
  const progressBar = document.createElement("div");
  progressBar.className = "section-progress-bar";

  // Create bullets
  const bullets = [];
  for (let i = 0; i < totalStates; i++) {
    const bullet = document.createElement("div");
    bullet.className = "section-progress-bullet";
    progressBar.appendChild(bullet);
    bullets.push(bullet);
  }

  // Append to section
  section.appendChild(progressBar);

  /**
   * Update progress bar based on state progress
   * @param {number} progress - Overall progress (0-1)
   * @param {number} currentState - Current state (1-4), optional
   */
  function updateProgress(progress, currentState = null) {
    if (!bullets.length) return;

    // Calculate progress per state (each state = 1/totalStates)
    const progressPerState = 1 / totalStates;

    bullets.forEach((bullet, index) => {
      const stateNumber = index + 1;
      let fillProgress = 0;

      // Calculate state boundaries
      const stateStart = (stateNumber - 1) * progressPerState;
      const stateEnd = stateNumber * progressPerState;

      if (currentState !== null) {
        // State-based progress: fill bullets based on current state
        if (stateNumber < currentState) {
          // Previous states: fully filled
          fillProgress = 100;
        } else if (stateNumber === currentState) {
          // Current state: fill based on overall progress within this state's range
          if (progress >= stateEnd) {
            fillProgress = 100;
          } else if (progress > stateStart) {
            // Calculate progress within current state
            const stateProgress = (progress - stateStart) / progressPerState;
            fillProgress = Math.max(0, Math.min(100, stateProgress * 100));
          } else {
            fillProgress = 0;
          }
        } else {
          // Future states: empty
          fillProgress = 0;
        }
      } else {
        // Overall progress: distribute across all states
        if (progress >= stateEnd) {
          // State fully completed
          fillProgress = 100;
        } else if (progress > stateStart) {
          // State in progress
          const stateProgress = (progress - stateStart) / progressPerState;
          fillProgress = stateProgress * 100;
        } else {
          // State not started
          fillProgress = 0;
        }
      }

      // Update bullet fill using CSS custom property
      bullet.style.setProperty("--fill-progress", `${fillProgress}%`);
    });
  }

  return {
    updateProgress,
    element: progressBar,
    destroy: () => {
      progressBar.remove();
    },
  };
}

// Make it globally available
window.createSectionProgressBar = createSectionProgressBar;

/**
 * Wait for critical images to load (above the fold, not lazy-loaded)
 */
function waitForAllImages() {
  return new Promise((resolve) => {
    const isMobile = window.innerWidth <= 999;

    // Only wait for images that are:
    // 1. Not lazy-loaded (no loading="lazy" attribute)
    // 2. Or already in viewport (above the fold)
    const allImages = document.querySelectorAll("img");
    const criticalImages = Array.from(allImages).filter((img) => {
      // Skip lazy-loaded images that aren't in viewport
      if (img.loading === "lazy") {
        const rect = img.getBoundingClientRect();
        const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;
        return isInViewport;
      }
      // Include all non-lazy images
      return true;
    });

    const totalImages = criticalImages.length;
    let loadedImages = 0;
    let hasError = false;

    if (totalImages === 0) {
      resolve();
      return;
    }

    const checkComplete = () => {
      loadedImages++;
      if (loadedImages === totalImages || hasError) {
        resolve();
      }
    };

    // On mobile, use shorter timeout (2 seconds) since we're not waiting for lazy images
    const timeout = isMobile ? 2000 : 5000;

    criticalImages.forEach((img) => {
      // If image is already loaded
      if (img.complete && img.naturalHeight !== 0) {
        checkComplete();
      } else {
        // Wait for load or error
        img.addEventListener("load", checkComplete, { once: true });
        img.addEventListener(
          "error",
          () => {
            hasError = true;
            checkComplete();
          },
          { once: true }
        );

        // Shorter timeout on mobile
        setTimeout(() => {
          if (loadedImages < totalImages) {
            hasError = true;
            checkComplete();
          }
        }, timeout);
      }
    });
  });
}

/**
 * Wait for critical scripts (GSAP, ScrollTrigger) - only on desktop
 */
function waitForCriticalScripts() {
  return new Promise((resolve) => {
    const isMobile = window.innerWidth <= 999;

    // On mobile, don't wait for GSAP at all
    if (isMobile) {
      resolve();
      return;
    }

    // Check if GSAP and ScrollTrigger are needed (desktop sections)
    const needsGSAP = document.querySelector(
      '[id*="box-section"], [id*="trimmer-section"], [id*="badges-section"], [id*="about-section"], [id*="benefits-section"]'
    );

    if (!needsGSAP) {
      // No sections that need GSAP, resolve immediately
      resolve();
      return;
    }

    // Wait for GSAP and ScrollTrigger (desktop only)
    const checkScripts = () => {
      if (typeof gsap !== "undefined" && typeof ScrollTrigger !== "undefined") {
        resolve();
      } else {
        // Retry after a short delay
        setTimeout(checkScripts, 100);
      }
    };

    // Start checking
    checkScripts();

    // Shorter timeout on desktop (5 seconds max)
    setTimeout(() => {
      resolve();
    }, 5000);
  });
}

/**
 * Hide preloader
 */
function hidePreloader() {
  const preloader = document.getElementById("preloader");
  if (preloader) {
    preloader.classList.add("hidden");
    // Restore body overflow after preloader animation starts
    document.body.style.overflow = "";
    document.body.classList.remove("preloader-active");
    document.documentElement.style.overflow = "";
    document.documentElement.classList.remove("preloader-active");
    // Dispatch custom event for sections to listen to
    document.dispatchEvent(new CustomEvent("preloaderFinished"));
    // Remove from DOM after animation
    setTimeout(() => {
      preloader.remove();
    }, 500);
  }
}

/**
 * Initialize preloader
 */
function initializePreloader() {
  if (preloaderInitialized) return;
  preloaderInitialized = true;

  // Set body overflow hidden to prevent scrolling and layout shift
  document.body.style.overflow = "hidden";
  document.body.classList.add("preloader-active");
  document.documentElement.style.overflow = "hidden";
  document.documentElement.classList.add("preloader-active");

  const isMobile = window.innerWidth <= 999;
  const startTime = Date.now();

  // Minimum display time to prevent flickering (shorter on mobile)
  const minDisplayTime = isMobile ? 300 : 500;

  // Wait for both images and scripts
  Promise.all([waitForAllImages(), waitForCriticalScripts()]).then(() => {
    imagesLoaded = true;
    scriptsReady = true;

    // Ensure minimum display time has passed
    const elapsed = Date.now() - startTime;
    const remainingTime = Math.max(0, minDisplayTime - elapsed);

    setTimeout(() => {
      hidePreloader();
    }, remainingTime);
  });
}

// Initialize preloader when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initializePreloader);
} else {
  initializePreloader();
}

// Also handle browser back/forward navigation
window.addEventListener("pageshow", (event) => {
  if (event.persisted) {
    // Page was restored from cache, reinitialize
    preloaderInitialized = false;
    imagesLoaded = false;
    scriptsReady = false;

    // Check if preloader exists in DOM
    const preloader = document.getElementById("preloader");

    if (preloader) {
      // Preloader exists, reinitialize normally
      initializePreloader();
    } else {
      // Preloader was already removed, skip it and restore state immediately
      // Restore body overflow
      document.body.style.overflow = "";
      document.body.classList.remove("preloader-active");
      document.documentElement.style.overflow = "";
      document.documentElement.classList.remove("preloader-active");

      // Ensure images are visible (remove any opacity 0 styles that might persist)
      document.querySelectorAll('img').forEach((img) => {
        // Remove inline opacity 0 if it exists
        if (img.style.opacity === '0') {
          img.style.opacity = '';
        }
        // Ensure visibility is not hidden
        if (img.style.visibility === 'hidden') {
          img.style.visibility = '';
        }
      });

      updateViewportHeight();
      initializeLazyImages();
      initializeThemeMeasurements();

      // Reset product form loading states (in case they're stuck)
      if (typeof resetAllProductFormsLoading === "function") {
        resetAllProductFormsLoading();
      }

      // Force product forms to reinitialize by removing initialization flag
      document.querySelectorAll("[data-product-form][data-form-initialized]").forEach((form) => {
        form.removeAttribute("data-form-initialized");
      });

      // Dispatch preloaderFinished event so sections can reinitialize
      // This will also trigger product form reinitialization
      document.dispatchEvent(new CustomEvent("preloaderFinished"));
    }
  }
});

document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll(".initially-hidden").forEach((el) => {
    el.classList.remove("initially-hidden");
  });
  // Set initial viewport height (for general use)
  updateViewportHeight();

  initializeLazyImages();
  initializeThemeMeasurements();
});

// General viewport height that updates (for other components)
function updateViewportHeight() {
  document.documentElement.style.setProperty(
    "--vh",
    `${window.innerHeight * 0.01}px`
  );
}

// Global lazy image loader with skeleton backgrounds
function initializeLazyImages() {
  const lazyImages = document.querySelectorAll(".lazy-fade");

  if (!lazyImages.length) return;

  lazyImages.forEach((img) => {
    // Add skeleton class to wrapper
    const wrapper = img.closest(".lazy-wrapper");
    if (wrapper) {
      wrapper.classList.add("loading");
    }

    // Check if already loaded
    if (img.complete && img.naturalHeight !== 0) {
      handleImageLoad(img, wrapper);
    } else {
      // Listen for load event
      img.addEventListener("load", function () {
        handleImageLoad(img, wrapper);
      });

      // Handle error
      img.addEventListener("error", function () {
        if (wrapper) {
          wrapper.classList.remove("loading");
          wrapper.classList.add("error");
        }
      });
    }
  });
}

function handleImageLoad(img, wrapper) {
  img.classList.add("loaded");
  if (wrapper) {
    wrapper.classList.remove("loading");
    wrapper.classList.add("loaded");
  }
}

function debounce(fn, delay) {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => {
      fn.apply(this, args);
    }, delay);
  };
}

function debounceImmediate(func, wait, immediate) {
  let timeout;
  return function () {
    const context = this,
      args = arguments;
    const later = function () {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };
}

function throttle(func, limit) {
  let lastFunc;
  let lastRan;
  return function () {
    const context = this;
    const args = arguments;
    if (!lastRan) {
      func.apply(context, args);
      lastRan = Date.now();
    } else {
      clearTimeout(lastFunc);
      lastFunc = setTimeout(() => {
        if (Date.now() - lastRan >= limit) {
          func.apply(context, args);
          lastRan = Date.now();
        }
      }, limit - (Date.now() - lastRan));
    }
  };
}

const html = document.documentElement;

function setElementHeight(element, { heightVar, onUpdate }) {
  if (!element) return;

  const height = element.offsetHeight;

  if (heightVar) {
    html.style.setProperty(heightVar, `${height}px`);
  }

  if (typeof onUpdate === "function") {
    onUpdate(height);
  }
}

function initializeAnnouncement() {
  const announcementBar = document.querySelector(".announcement-bar");
  if (!announcementBar) return;

  const updateAnnouncement = () =>
    setElementHeight(announcementBar, {
      heightVar: "--announcement-height",
      onUpdate: (height) => {
        window.announcementHeight = height;
      },
    });

  updateAnnouncement();

  if (typeof ResizeObserver !== "undefined") {
    const observer = new ResizeObserver(updateAnnouncement);
    observer.observe(announcementBar);
  } else {
    const debouncedAnnouncement = debounce(updateAnnouncement, 300);
    window.addEventListener("resize", debouncedAnnouncement, {
      passive: true,
    });
  }
}

function initializeHeaderHeight() {
  const mainHeader = document.querySelector(".main-header");
  if (!mainHeader) return;

  const updateHeader = () =>
    setElementHeight(mainHeader, {
      heightVar: "--header-height",
      onUpdate: (height) => {
        window.headerHeight = height;
      },
    });

  updateHeader();

  const throttledHeaderUpdate = throttle(updateHeader, 16);
  window.addEventListener("scroll", throttledHeaderUpdate, {
    passive: true,
  });

  if (typeof ResizeObserver !== "undefined") {
    const observer = new ResizeObserver(updateHeader);
    observer.observe(mainHeader);
  } else {
    const debouncedHeaderUpdate = debounce(updateHeader, 150);
    window.addEventListener("resize", debouncedHeaderUpdate, {
      passive: true,
    });
  }
}

function initializeThemeMeasurements() {
  initializeAnnouncement();
  initializeHeaderHeight();
}

// Helper: Calculate luminance (brightness) of a color based on rgb values like this ' 255, 255 , 255 '
function getLuminance(colorStr) {
  const [r, g, b] = colorStr.split(",").map(Number);
  return (r * 299 + g * 587 + b * 114) / 1000; // Weighted luminance
}
